// Pure helpers for the optional "Test device & check reading" panel.
// The backend is authoritative for whether a report qualifies; these only
// validate the response shape and describe results honestly.

import { canonicalJson } from "../claim/operationsPlan";

export const TEST_LIMIT_MS = 180000;
export const TEST_TERMINAL = ["received", "timed_out", "cancelled", "failed"];

export function checkedTestResult(value, deviceId, testId) {
  if (!value || typeof value.test_id !== "string" ||
      Number(value.device_id) !== Number(deviceId) ||
      (testId && value.test_id !== testId) ||
      !["waiting", ...TEST_TERMINAL].includes(value.status) ||
      !Number.isFinite(Date.parse(value.expires_at)) ||
      !Number.isFinite(Date.parse(value.server_now))) {
    throw new Error("The device test returned an invalid response.");
  }
  if (value.status === "received" && !value.observation) {
    throw new Error("The received device test has no observation.");
  }
  return value;
}

// Binds a test (and any confirmation) to the exact settings it previewed.
export function testSnapshotKey(calibration, geometry) {
  return canonicalJson({ calibration: calibration || null, geometry: geometry || null });
}

export function formatCountdown(seconds) {
  const safe = Math.max(0, Math.floor(Number(seconds) || 0));
  return `${String(Math.floor(safe / 60)).padStart(2, "0")}:${String(safe % 60).padStart(2, "0")}`;
}

// A reported number, or null. Never coerces missing data or strings such as
// "Out of Range" into 0.
export function reportedNumber(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value !== "string" || value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function reportedText(value, unit, digits = 1) {
  const number = reportedNumber(value);
  return number === null ? "Not reported" : `${number.toFixed(digits)}${unit ? ` ${unit}` : ""}`;
}

// Review / history wording for the wizard's retained test record.
export function deviceTestLabel(record) {
  if (!record) return "Not performed";
  if (record.stale) return "Settings changed after the test - not verified";
  switch (record.status) {
    case "received":
      return record.measurement_confirmed
        ? "Reading checked by installer"
        : "Fresh report received; measurement not confirmed";
    case "timed_out":
      return "Timed out / not verified";
    case "cancelled":
      return "Cancelled / not verified";
    case "failed":
      return "Test failed / not verified";
    case "waiting":
      return "Stopped before a report arrived / not verified";
    default:
      return "Not verified";
  }
}
